import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

import { Logo } from '../../components/Logo';

import { ApiError } from '../../api/http';
import { authApi } from '../../api/auth';
import { logError } from '../../lib/logger';

type LinkState = { kind: 'checking' } | { kind: 'valid'; email: string } | { kind: 'invalid' };

/**
 * 用重設連結設定新密碼。
 *
 * 開場先問一次 token 還能不能用，讓「連結已失效」在使用者填完密碼**之前**就講清楚——
 * 填完才被拒是最讓人惱火的一種失敗。過期／用過／不存在後端都回同一個 404，
 * 所以這裡也只有一種失效畫面。
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const [link, setLink] = useState<LinkState>({ kind: 'checking' });
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (token === undefined) {
      setLink({ kind: 'invalid' });
      return;
    }

    // 元件在回應到達前就被卸載時（使用者按了返回）不要對死掉的元件 setState
    let active = true;

    authApi
      .previewReset(token)
      .then((preview) => {
        if (active) {
          setLink({ kind: 'valid', email: preview.email });
        }
      })
      .catch((caught: unknown) => {
        if (!(caught instanceof ApiError)) {
          logError('確認密碼重設連結失敗', caught);
        }

        if (active) {
          setLink({ kind: 'invalid' });
        }
      });

    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (token === undefined) {
      return;
    }

    setError(null);
    setPending(true);

    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('auth_error_unknown'));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-primary/5 p-6">
      <div className="w-full max-w-[420px] rounded-xl bg-card p-6 shadow-card ring-1 ring-foreground/10">
        <Logo size={34} />

        {done ? (
          <>
            <h1 className="mt-3 mb-1 text-2xl font-bold tracking-tight">
              {t('auth_reset_done_title')}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">{t('auth_reset_done_body')}</p>
            <Link to="/login" className={buttonVariants()}>
              {t('auth_login_submit')}
            </Link>
          </>
        ) : link.kind === 'checking' ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {t('auth_reset_checking')}
          </p>
        ) : link.kind === 'invalid' ? (
          <>
            <h1 className="mt-3 mb-1 text-2xl font-bold tracking-tight">
              {t('auth_reset_invalid_title')}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">{t('auth_reset_invalid_body')}</p>
            <Link to="/forgot-password" className={buttonVariants()}>
              {t('auth_reset_invalid_action')}
            </Link>
          </>
        ) : (
          <form onSubmit={(event) => void onSubmit(event)}>
            <h1 className="mt-3 mb-1 text-2xl font-bold tracking-tight">{t('auth_reset_title')}</h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {t('auth_reset_subtitle', { email: link.email })}
            </p>

            <div className="flex flex-col gap-3">
              {error !== null && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Field>
                <FieldLabel htmlFor="reset-password">{t('auth_reset_new_password')}</FieldLabel>
                <FieldDescription>{t('auth_reset_hint')}</FieldDescription>
                <Input
                  id="reset-password"
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                />
              </Field>

              <Button type="submit" disabled={pending}>
                {pending && <Spinner data-icon="inline-start" />}
                {t('auth_reset_submit')}
              </Button>
            </div>
          </form>
        )}

        {!done && (
          <p className="mt-4 text-sm text-muted-foreground">
            <Link to="/login" className="text-primary underline underline-offset-4">
              {t('auth_back_to_login')}
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
